import React, { useState } from "react";

export interface CoreExpanderProps {
  title: string;
  titlePrefix?: string;
  children?: React.ReactNode;
  /** When true, section starts expanded. */
  defaultExpanded?: boolean;
  /** Rendered at the end of the header row, e.g. an "Expand Table" button. */
  headerAction?: React.ReactNode;
}

const CHEVRON_SIZE = 16;

const CoreExpanderChevron = ({ expanded }: { expanded: boolean }) => (
  <svg
    width={CHEVRON_SIZE}
    height={CHEVRON_SIZE}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={`shrink-0 text-chat-dark transition-transform duration-200 ${
      expanded ? "rotate-90" : ""
    }`}
    aria-hidden
  >
    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
  </svg>
);

/**
 * Collapsible section: chevron + bold title; body indented to align with title text.
 */
const CoreExpander: React.FC<CoreExpanderProps> = ({
  title,
  titlePrefix,
  children,
  defaultExpanded = false,
  headerAction,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="relative mb-3 w-full min-w-0 bg-white">
      <div className="flex w-full items-start gap-2 px-3 py-2.5">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-start gap-2 text-left hover:bg-gray-50/80"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <span
            data-core-expander-chevron
            className="mt-0.5 flex w-4 shrink-0 justify-center"
          >
            <CoreExpanderChevron expanded={expanded} />
          </span>
          <span className="min-w-0 flex-1 font-manrope text-md font-bold leading-snug text-chat-dark">
            {titlePrefix ? `${titlePrefix} ` : ""}
            {title || "Section"}
          </span>
        </button>

        {headerAction ? (
          <span className="mt-0.5 shrink-0">{headerAction}</span>
        ) : null}
      </div>

      {/* Always mount body so copy/PDF export (innerHTML) includes collapsed sections; visually hide when collapsed.
          data-core-expander-* lets PDF export strip clip/absolute from a clone so nested tables still layout. */}
      <div
        data-core-expander-body
        {...(!expanded ? { "data-core-expander-collapsed": "" } : {})}
        className={
          expanded
            ? "flex gap-2 border-t border-gray-100 px-3 pb-3 pt-2"
            : "pointer-events-none flex gap-2 border-t border-gray-100 px-3 pb-3 pt-2"
        }
        style={
          expanded
            ? undefined
            : {
                position: "absolute",
                width: "1px",
                height: "1px",
                padding: 0,
                margin: "-1px",
                overflow: "hidden",
                clip: "rect(0, 0, 0, 0)",
                whiteSpace: "nowrap",
                borderWidth: 0,
                borderStyle: "none",
              }
        }
        {...(!expanded ? { "aria-hidden": true, inert: true } : {})}
      >
        <div className="w-4 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1 text-hex-text-dark font-manrope text-md font-medium leading-[140%] [&_strong]:font-bold [&_strong]:text-chat-dark">
          {children}
        </div>
      </div>
    </div>
  );
};

export default CoreExpander;
